import isToday from 'date-fns/isToday';
import throttle from 'lodash/throttle';
import { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { useStoreActions, useStoreState } from '~/store/hooks';
import useRole from '~/store/user/hooks/useRole';
import UtilizationModule from '~/store/utilization/constants/utilizationModule';
import { setNoon } from '~/utils/date/date';
import { extractErrorMessage } from '~/utils/error/error';

const DEBOUNCE_DELAY = 1000;
const MILL_SEC_COEFF = 60 * 1000;
const INACTIVITY_TIMEOUT = 1000 * 60 * 3; // 3 mins

let loading = false; // module scope variable to have single loading value for all possible instances of useUtilization

const useUtilization = (): {
  timeout: number;
  resumeUtilization: () => void;
  stopUtilization: () => void;
} => {
  const location = useLocation();

  const { setUtilizationModule, onSendUtilizationData } = useStoreActions(
    actions => actions.utilization,
  );
  const showError = useStoreActions(actions => actions.snackbar.showError);

  const { current } = useStoreState(state => state.user);
  const { users } = useStoreState(state => state.utilization);

  const { isActTeam, isGlobalUser } = useRole();

  const utilizationAvailable = isActTeam || isGlobalUser;
  const teamId = isActTeam ? current?.teamId : null;
  const userId = current?.id;
  const clinicId = current?.clinic?.id;

  const resumeUtilization = useCallback(
    () => {
      throttle(() => {
        if (userId && location?.pathname && utilizationAvailable) {
          setUtilizationModule({ route: location.pathname, teamId, userId });
        }
      }, DEBOUNCE_DELAY)();
    }, // Adjust throttle time (e.g., 5000ms = 5 seconds)
    [userId, location.pathname, utilizationAvailable, setUtilizationModule, teamId],
  );

  const stopUtilization = useCallback(() => {
    throttle(() => {
      setUtilizationModule(null);
    }, DEBOUNCE_DELAY)();
  }, [setUtilizationModule]);

  useEffect(() => {
    resumeUtilization();
  }, [resumeUtilization]);

  const syncUtilizationData = useCallback(() => {
    if (loading) {
      return;
    }
    const userKey = teamId ? `${userId}_${teamId}` : `${userId}`;
    const utilizationData = users[userKey];
    if (utilizationData && clinicId && utilizationAvailable) {
      Object.keys(utilizationData).forEach(async date => {
        const utcDate = new Date(setNoon(date));

        const localDate = new Date(
          utcDate.getTime() + utcDate.getTimezoneOffset() * MILL_SEC_COEFF * -1,
        );
        if (isToday(localDate)) {
          // BE overwrite utilization data for the same day, so this condition allows to send data only once per day
          return;
        }
        const modules = Object.entries(utilizationData[date]).map(([key, value]) => ({
          teamId,
          module: key as UtilizationModule,
          usageSeconds: (value as { timeSpent: number }).timeSpent,
        }));

        try {
          loading = true;
          const data = { date, modules };
          await onSendUtilizationData({ clinicId, teamId, userId, data });
        } catch (e) {
          showError(extractErrorMessage(e));
        } finally {
          loading = false;
        }
      });
    }
  }, [clinicId, onSendUtilizationData, showError, teamId, userId, users, utilizationAvailable]);

  useEffect(() => {
    syncUtilizationData();
  }, [syncUtilizationData]);

  return { timeout: INACTIVITY_TIMEOUT, resumeUtilization, stopUtilization };
};

export default useUtilization;
